import { AfterViewInit, Component, DestroyRef, Inject, OnInit, Optional, ViewChild, inject, isDevMode, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { UntypedFormGroup } from "@angular/forms";
import { MatTabChangeEvent, MatTabGroup, MatTab } from "@angular/material/tabs";

import { DffCurrentRoute, DffRoute } from "@dffedb/router";
import { LocalStorage, compareDanish, trim } from "@dffedb/util";
import { AuthenticationService, HuskMigCredentialsService, LoginService, TeknikerPasswordCapabilityService } from "@e-forsyning/common/auth";
import { VaelgInstallationService } from "@e-forsyning/common/installation";
import { RedirectService } from "@e-forsyning/common/router";

import { LoginEmailComponent } from "../login-email/login-email.component";
import { LoginForbrugernrComponent } from "../login-forbrugernr/login-forbrugernr.component";
import { LoginFormBuilder } from "../login-form-builder";
import { LoginContainerComponent } from "../login-container/login-container.component";
import { LoginInput } from "../login-input/login-input";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatIconModule } from "@angular/material/icon";
import { BiometricService } from "../biometrics/biometrics.service";
import { firstValueFrom, forkJoin, of, Subject, EMPTY } from "rxjs";
import { catchError, finalize, map, switchMap, take, tap } from "rxjs/operators";
import { first } from "rxjs";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { UmbracoIndstillingerRepository } from "@e-forsyning/common/umbraco-indstillinger/umbraco-indstillinger.repository";
import { UmbracoHttpClient } from "@e-forsyning/common/http/umbraco-http-client.service";
import { UmbracoMenuObserver } from "@e-forsyning/common/eforsyning-menu/observers/umbraco-menu.observer";
import { UmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger/model/umbraco-indstillinger.model";
import { EforsyningIndstilling } from "@e-forsyning/common/umbraco-indstillinger/model/e-forsyning-indstilling.model";
import { MenuItem } from "@dffedb/ui";
import { APP_SHELL_LAYOUT_OPTIONS, AppShellLayoutOptions } from "@e-forsyning/common/app-shell";
import { ServiceProFunctionalConsentService } from "@e-forsyning/common/service-pro/cookie-consent";
import { ServiceProCookieConsentSandbox } from "@e-forsyning/common/service-pro/cookie-consent/service-pro-cookie-consent.sandbox";
import { getLoginRouteByPreference, isTeknikerContext, SERVICEPRO_TEKNIKER_LOGIN_ROUTE, TECHNICIAN_LOGIN_ROUTE } from "@e-forsyning/common/util";
import { LoginHjaelpService } from "../login-hjaelp/login-hjaelp.service";
import { resolveTeknikerLoginMatomoChannel } from "../login-matomo.util";
import { MatomoTracker } from "ngx-matomo-client";

interface TeknikerVaerkResponse {
    id: number;
    name?: string;
    firmaNavn?: string;
    appServerUri?: string;
    url?: string;
    forsyningId?: string;
    eforsyninger?: Array<{
        id: number;
        name?: string;
        appServerUri?: string;
        url?: string;
        forsyningId?: string;
    }>;
    Id?: number;
    Name?: string;
    FirmaNavn?: string;
    AppServerUri?: string;
    Url?: string;
    ForsyningId?: string;
    Eforsyninger?: Array<{
        Id?: number;
        Name?: string;
        AppServerUri?: string;
        Url?: string;
        ForsyningId?: string;
    }>;
}

interface EforsyningOption {
    id: number | null;
    label: string;
    url?: string;
    appServerUrl?: string;
    forsyningId?: string;
}

interface ChosenPlant {
    forsyningId: string;
    name: string;
    eforsyningId?: number;
    url?: string;
    appServerUrl?: string;
}

@Component({
    selector: "login-technician",
    imports: [
        LoginContainerComponent,
        LoginInput,
        CommonModule,
        FormsModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule
        // MatTabGroup, MatTab, LoginForbrugernrComponent, LoginEmailComponent,
    ],
    templateUrl: "./login-technician.html",
    styleUrls: ["../login.component.scss", "./login-technician.scss"]
})
export class LoginTechnician implements OnInit, AfterViewInit {
    private readonly destroyRef = inject(DestroyRef);
    private readonly instanceId = Math.random().toString(36).slice(2, 8);
    private readonly chosenPlantStorageKey = "chosenPlant";
    private chosenPlant: ChosenPlant | null = null;
    private readonly passwordCapabilityTrigger$ = new Subject<{
        forsyningId: string;
        appServerUrl: string;
        forceRefresh?: boolean;
    } | null>();
    public skipVaerksliste = false;
    private vaerkslisteRequestStartedAt: number | null = null;

    public form: UntypedFormGroup;
    public loginFejl = false;
    public fejlBesked = "";

    public isBiometricAvailable = false;
    public biometricActionText = "";
    public biometricEnabled = false;
    public biometricLoginInProgress = false;

    public eforsyningOptions: EforsyningOption[] = [];
    public vaerkSearch = "";
    public vaerkSelection: EforsyningOption | string | null = null;
    public selectedEforsyningId: number | null = null;
    public selectedForsyningId: string | null = null;
    public selectedAppServerUrl: string | null = null;
    public selectedSiteUrl: string | null = null;
    public technicianContactPhone = "12 13 14 15";
    /** Sat ved værkvalg via api/teknikerpwdfeatures (styrer glemt-/skift-felter på login). */
    public teknikerPasswordFeaturesSupported = false;
    public readonly loadingVaerker = signal(false);
    public readonly loadingIndstillinger = signal(false);
    public readonly loadingPasswordFeatures = signal(false);
    public readonly loginArbejder = signal(false);
    public vaerkFejlbesked = "";

    @ViewChild(MatTabGroup, { static: true }) public tabGroup: MatTabGroup;
    @ViewChild(LoginEmailComponent, { static: true }) public loginEmailComponent: LoginEmailComponent;
    @ViewChild(LoginForbrugernrComponent, { static: true }) public loginForbrugernrComponent: LoginForbrugernrComponent;
    @ViewChild(LoginInput, { static: true }) public loginInput: LoginInput;

    constructor(
        private readonly currentRoute: DffCurrentRoute,
        private readonly huskMigCredentialsService: HuskMigCredentialsService,
        private readonly redirectService: RedirectService,
        private readonly localStorage: LocalStorage,
        private readonly loginService: LoginService,
        private readonly authenticationService: AuthenticationService,
        private readonly vaelgInstallationService: VaelgInstallationService,
        private readonly biometricService: BiometricService,
        private readonly umbracoIndstillingerObserver: UmbracoIndstillingerObserver,
        private readonly umbracoMenuObserver: UmbracoMenuObserver,
        private readonly umbracoIndstillingerRepository: UmbracoIndstillingerRepository,
        private readonly umbracoHttpClient: UmbracoHttpClient,
        private readonly functionalConsentService: ServiceProFunctionalConsentService,
        private readonly cookieConsentSandbox: ServiceProCookieConsentSandbox,
        private readonly loginHjaelpService: LoginHjaelpService,
        private readonly matomoTracker: MatomoTracker,
        private readonly teknikerPasswordCapability: TeknikerPasswordCapabilityService,
        @Optional() @Inject(APP_SHELL_LAYOUT_OPTIONS) private readonly appShellLayout: AppShellLayoutOptions | null
    ) {
        this.passwordCapabilityTrigger$
            .pipe(
                switchMap((ctx) => {
                    if (!ctx) {
                        this.teknikerPasswordFeaturesSupported = false;
                        this.loadingPasswordFeatures.set(false);
                        return EMPTY;
                    }
                    this.loadingPasswordFeatures.set(true);
                    return this.teknikerPasswordCapability
                        .supportsTeknikerPasswordFeatures$({
                            forsyningId: ctx.forsyningId,
                            appServerUrl: ctx.appServerUrl,
                            forceRefresh: ctx.forceRefresh
                        })
                        .pipe(finalize(() => this.loadingPasswordFeatures.set(false)));
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((supported) => {
                this.teknikerPasswordFeaturesSupported = supported;
                this.logVaerksliste("passwordCapability", { supported });
            });
    }

    /** Aktiveres i dev eller med `localStorage.setItem('debug-vaerksliste', 'true')`. Filtrér konsol på `[servicepro-vaerksliste]`. */
    private logVaerksliste(message: string, data?: Record<string, unknown>): void {
        const enabled = isDevMode() || (typeof localStorage !== "undefined" && localStorage.getItem("debug-vaerksliste") === "true");
        if (!enabled) {
            return;
        }
        const payload = {
            instance: this.instanceId,
            loadingVaerker: this.loadingVaerker(),
            loadingIndstillinger: this.loadingIndstillinger(),
            loadingPasswordFeatures: this.loadingPasswordFeatures(),
            teknikerPasswordFeaturesSupported: this.teknikerPasswordFeaturesSupported,
            optionCount: this.eforsyningOptions.length,
            selectedEforsyningId: this.selectedEforsyningId,
            erKlarTilLogin: this.erKlarTilLogin,
            ...data
        };
    }

    public ngOnInit(): void {
        this.logVaerksliste("ngOnInit start", {
            path: trim(this.currentRoute.value.path, "/"),
            chosenPlant: this.chosenPlant
        });
        this.form = LoginFormBuilder.opretForm(this.huskMigCredentialsService);

        this.chosenPlant = this.readChosenPlant();
        if (this.chosenPlant?.name) {
            this.vaerkSearch = this.chosenPlant.name;
            this.vaerkSelection = this.chosenPlant.name;
        }
        const erLoggetInd = this.loginService.erLoggetInd();
        const sessionAktiv = erLoggetInd && !this.authenticationService.isAuthenticationTimeout();
        if (sessionAktiv && this.umbracoIndstillingerObserver.initialized) {
            this.logVaerksliste("ngOnInit: gyldig session – redirect efter login");
            this.skipVaerksliste = true;
            this.navigateToLoginPage();
            if (this.loginService.skalSkifteKodeord()) {
                this.redirectService.redirect("/skift-kodeord");
            } else {
                this.redirectAfterLogin();
            }
            return;
        }
        if (erLoggetInd) {
            this.logVaerksliste("ngOnInit: forældet/ugyldig session (cryptId uden aktiv auth) – logger ud");
            this.loginService.logout();
        }

        // På eForsyning er værket allerede bestemt af URL-konteksten (fx /demonoweb/#/tekniker-login).
        // Umbraco-indstillinger indlæses under APP_INITIALIZER, så vi kan skippe værksvælgeren.
        if (this.umbracoIndstillingerObserver.initialized && !this.appShellLayout?.teknikerLoginRoute) {
            this.tryUseEforsyningContext();
        }

        this.navigateToLoginPage();
        this.logVaerksliste("ngOnInit færdig");
    }

    public ngAfterViewInit(): void {
        if (this.isServiceProLogin()) {
            void this.cookieConsentSandbox.promptIfNeeded();
        }

        if (this.skipVaerksliste) {
            this.logVaerksliste("ngAfterViewInit: skipVaerksliste");
            void this.initBiometricStatus();
            return;
        }
        // Efter router/navigation er færdig – undgår at HTTP annulleres ved redirect fra `/`.
        setTimeout(() => {
            if (this.skipVaerksliste) {
                return;
            }
            void this.loadVaerkOnLogin();
        }, 0);
        void this.initBiometricStatus();
    }

    public onEforsyningChange(value: string | number | null): void {
        const id = typeof value === "string" ? parseInt(value, 10) : value;
        if (!id || isNaN(id)) {
            this.selectedEforsyningId = null;
            return;
        }

        this.selectedEforsyningId = id;
        this.seedUmbracoIndstillinger(id, this.selectedForsyningId || null);
        this.refreshTeknikerPasswordCapability(this.selectedForsyningId, this.selectedAppServerUrl);
        this.hentUmbracoIndstillinger(id);
    }

    public onVaerkSelectionChange(value: EforsyningOption | string | null): void {
        if (typeof value === "string") {
            this.vaerkSearch = value || "";
            const normalizedQuery = this.vaerkSearch.trim().toLocaleLowerCase();
            const exactMatch = this.eforsyningOptions.find((option) => option.label.trim().toLocaleLowerCase() === normalizedQuery);
            if (exactMatch) {
                this.onVaerkOptionSelected(exactMatch);
                return;
            }
            if (!this.vaerkSearch) {
                this.clearTeknikerPasswordCapability();
                this.selectedEforsyningId = null;
            }
            return;
        }

        if (value) {
            this.onVaerkOptionSelected(value);
            return;
        }

        this.vaerkSearch = "";
        this.clearTeknikerPasswordCapability();
        this.selectedEforsyningId = null;
    }

    public clearVaerkSearch(): void {
        this.vaerkSearch = "";
        this.vaerkSelection = null;
        this.selectedEforsyningId = null;
        this.selectedForsyningId = null;
        this.selectedAppServerUrl = null;
        this.selectedSiteUrl = null;
        this.clearTeknikerPasswordCapability();
        this.chosenPlant = null;
        this.eforsyningOptions = [];
        this.localStorage.removeItem(this.chosenPlantStorageKey);
        if (!this.loadingVaerker()) {
            this.hentVaerksliste();
        }
    }

    public onVaerkOptionSelected(option: EforsyningOption): void {
        if (!option) {
            this.selectedEforsyningId = null;
            this.clearTeknikerPasswordCapability();
            return;
        }

        this.logVaerksliste("onVaerkOptionSelected", {
            label: option.label,
            eforsyningId: option.id,
            forsyningId: option.forsyningId
        });

        this.technicianContactPhone = "";
        this.vaerkSearch = option.label;
        this.vaerkSelection = option;
        this.vaerkFejlbesked = "";
        this.selectedForsyningId = option.forsyningId || null;
        this.selectedAppServerUrl = option.appServerUrl || null;
        this.selectedSiteUrl = option.url || null;
        if (option.id === null || option.id === undefined) {
            this.selectedEforsyningId = null;
            this.clearTeknikerPasswordCapability();
            this.vaerkFejlbesked = "Værket mangler et gyldigt id.";
            return;
        }

        this.selectedEforsyningId = option.id;
        this.persistChosenPlant(option);
        this.seedUmbracoIndstillinger(option.id, option.forsyningId || null, option.url, option.appServerUrl);
        this.refreshTeknikerPasswordCapability(option.forsyningId, option.appServerUrl);
        this.hentUmbracoIndstillinger(option.id);
    }

    public displayVaerk(option: EforsyningOption | string | null): string {
        if (typeof option === "string") {
            return option;
        }
        return option?.label || "";
    }

    public get filteredEforsyningOptions(): EforsyningOption[] {
        const query = (this.vaerkSearch || "").trim().toLowerCase();
        if (!query) {
            return this.eforsyningOptions;
        }
        return this.eforsyningOptions.filter((option) => option.label.toLowerCase().includes(query));
    }

    public get erKlarTilLogin(): boolean {
        return !!this.selectedEforsyningId && !this.loadingIndstillinger() && !this.loadingPasswordFeatures();
    }

    public onLoginArbejderChange(arbejder: boolean): void {
        this.loginArbejder.set(arbejder);
    }

    public navigateToLoginPage() {
        const loginPage = this.localStorage.getItem("sidste-login-side"); // "tekniker" | "forbruger" | null
        const loginTarget = this.resolveTeknikerLoginTarget(loginPage);

        // Hent aktuel path (uden hash og uden leading slash)
        const currentPath = trim(this.currentRoute.value.path, "/");
        const targetPath = trim(loginTarget, "/");

        // Hvis vi ikke allerede står på korrekt login-side, naviger derhen
        // (allow nested paths like login/xyz to count som samme gren)
        if (!currentPath.startsWith(targetPath)) {
            this.logVaerksliste("navigateToLoginPage: redirect", { currentPath, targetPath });
            this.redirectService.redirect(targetPath);
            return;
        }
        this.logVaerksliste("navigateToLoginPage: ingen redirect nødvendig", { currentPath, targetPath });
    }

    private resolveTeknikerLoginTarget(lastLoginSide: string | null): string {
        const configured = this.appShellLayout?.teknikerLoginRoute?.trim();
        if (configured) {
            return configured.startsWith("/") ? configured : `/${configured}`;
        }
        if (isTeknikerContext()) {
            return TECHNICIAN_LOGIN_ROUTE;
        }
        return getLoginRouteByPreference(lastLoginSide, false);
    }

    public async onLoginSucceeded(): Promise<void> {
        if (this.loginService.skalSkifteKodeord()) {
            this.redirectService.redirect("/skift-kodeord");
            return;
        }
        sessionStorage.setItem("biometricAutoLoginDone", "true");
        await this.vaelgInstallationService.vaelgFoeresteInstallation(false, this.form.value.forbrugerNrGroup.forbrugerNr);
        this.redirectAfterLogin();
    }

    public redirect(): void {
        const { path, params } = this.getRedirectPath();
        if (path && path !== "404") {
            this.redirectService.redirect(path, params);
        } else {
            this.redirectService.redirectTilDefault();
        }
    }

    private redirectAfterLogin(): void {
        const { path, params } = this.getRedirectPath();
        if (path && path !== "404") {
            this.redirectService.redirect(path, params);
        } else {
            this.redirectService.redirectTilDefaultLanding();
        }
    }

    public onSelectedTabChange(event: MatTabChangeEvent): void {
        this.focusTabContent(event.index);
    }

    private focusTabContent(tabIndex: number): void {
        if (tabIndex === 0) {
            this.loginForbrugernrComponent.focus();
        } else {
            this.loginEmailComponent.focus();
        }
    }

    private getRedirectPath(): DffRoute {
        // Fjern login-root fra url'en og returner resten.
        // Eksempel: login/mine-oplysninger eller servicepro-login/mine-oplysninger -> mine-oplysninger
        const { path, params } = this.currentRoute.value;
        const segments = trim(path, "/").split("/").filter(Boolean);
        const loginRoots = ["login", "servicepro-login", "tekniker-login"];
        if (segments.length && loginRoots.includes(segments[0])) {
            return { params, path: segments.slice(1).join("/") };
        }
        return { params, path: segments.join("/") };
    }

    /**
     * På eForsyning er værket allerede bestemt af Umbraco-konteksten (URL path før #).
     * Sætter selectedEforsyningId direkte fra UmbracoIndstillinger og springer værksvælgeren over.
     */
    private tryUseEforsyningContext(): void {
        const indstillinger = this.umbracoIndstillingerObserver;
        if (!indstillinger.initialized) {
            return;
        }
        indstillinger.valueChanges.pipe(take(1)).subscribe((settings) => {
            const nodeId = settings?.aktuelEforsyningNodeId;
            if (!nodeId) {
                this.logVaerksliste("tryUseEforsyningContext: ingen aktuelEforsyningNodeId");
                return;
            }
            this.logVaerksliste("tryUseEforsyningContext: bruger Umbraco-kontekst", { nodeId });
            this.skipVaerksliste = true;
            this.selectedEforsyningId = nodeId;
            const aktuel = settings.aktuelEforsyning;
            if (aktuel) {
                this.selectedForsyningId = aktuel.forsyningId || null;
                this.selectedAppServerUrl = aktuel.appServerUrl || null;
                this.technicianContactPhone = aktuel.firmaInfo?.telefon || "";
            }
            this.refreshTeknikerPasswordCapability(this.selectedForsyningId, this.selectedAppServerUrl);
        });
    }

    private async loadVaerkOnLogin(): Promise<void> {
        const restored = await this.tryRestoreStoredVaerkWithoutVaerksliste();
        if (restored) {
            return;
        }
        this.logVaerksliste("loadVaerkOnLogin: starter hentVaerksliste");
        this.hentVaerksliste();
    }

    public loginHjaelpClick(): void {
        this.loginHjaelpService.visLoginHjaelpServiceProDialog();
    }

    /** Genbruger gemt værk (localStorage / Umbraco-indstillinger) uden GetVaerkerMedEforsyningerForTekniker. */
    private async tryRestoreStoredVaerkWithoutVaerksliste(): Promise<boolean> {
        const option = await this.buildStoredVaerkOption();
        if (!option?.id) {
            return false;
        }
        this.logVaerksliste("tryRestoreStoredVaerkWithoutVaerksliste: springer værksliste over", {
            label: option.label,
            eforsyningId: option.id,
            forsyningId: option.forsyningId
        });
        this.eforsyningOptions = [option];
        this.onVaerkOptionSelected(option);
        return true;
    }

    private async buildStoredVaerkOption(): Promise<EforsyningOption | null> {
        const chosen = this.chosenPlant;
        if (!chosen) {
            return null;
        }

        if (chosen.eforsyningId) {
            return {
                id: chosen.eforsyningId,
                label: chosen.name,
                forsyningId: chosen.forsyningId,
                url: chosen.url,
                appServerUrl: chosen.appServerUrl
            };
        }

        const indstillinger = await firstValueFrom(this.umbracoIndstillingerObserver.valueChanges.pipe(take(1)));
        const match = (indstillinger?.eforsyninger || []).find((eforsyning) => String(eforsyning.forsyningId ?? "").trim() === chosen.forsyningId);
        if (!match?.nodeId) {
            return null;
        }

        return {
            id: match.nodeId,
            label: chosen.name,
            forsyningId: chosen.forsyningId,
            url: match.siteUrl,
            appServerUrl: match.appServerUrl
        };
    }

    private hentVaerksliste(): void {
        this.vaerkslisteRequestStartedAt = performance.now();
        this.loadingVaerker.set(true);
        this.vaerkFejlbesked = "";
        this.logVaerksliste("hentVaerksliste: HTTP start", {
            url: `${typeof window !== "undefined" ? window.location.origin : ""}/umbraco/dff/dffapi/GetVaerkerMedEforsyningerForTekniker`
        });
        // dffapi/WebAPI-plugin `dff` – `/umbraco/api/TeknikerAppApi/...` giver typisk 404 bag reverse proxy eller uens routing.
        this.umbracoHttpClient
            .get<unknown>("GetVaerkerMedEforsyningerForTekniker")
            .pipe(
                tap((response) => {
                    const durationMs = this.vaerkslisteRequestStartedAt ? Math.round(performance.now() - this.vaerkslisteRequestStartedAt) : null;
                    this.logVaerksliste("hentVaerksliste: HTTP svar modtaget", {
                        durationMs,
                        responseType: Array.isArray(response) ? "array" : typeof response,
                        responseKeys: response && typeof response === "object" && !Array.isArray(response) ? Object.keys(response as object) : undefined,
                        rawCount: Array.isArray(response) ? response.length : undefined
                    });
                }),
                map((response) => this.normalizeVaerkerResponse(response)),
                tap((vaerker) => {
                    this.logVaerksliste("hentVaerksliste: normaliseret", { vaerkCount: vaerker.length });
                }),
                map((vaerker) => this.mapVaerker(vaerker)),
                tap((options) => {
                    this.logVaerksliste("hentVaerksliste: mappet til options", {
                        labels: options.map((o) => o.label).slice(0, 5),
                        total: options.length
                    });
                }),
                catchError((error) => {
                    this.logVaerksliste("hentVaerksliste: HTTP fejl", {
                        status: error?.status,
                        message: error?.message,
                        url: error?.url
                    });
                    this.vaerkFejlbesked = "Kunne ikke hente værkslisten. Prøv igen.";
                    return of([] as EforsyningOption[]);
                }),
                finalize(() => {
                    const durationMs = this.vaerkslisteRequestStartedAt ? Math.round(performance.now() - this.vaerkslisteRequestStartedAt) : null;
                    this.loadingVaerker.set(false);
                    this.logVaerksliste("hentVaerksliste: finalize (loadingVaerker=false)", { durationMs });
                })
            )
            .subscribe((options) => {
                this.eforsyningOptions = options;
                this.logVaerksliste("hentVaerksliste: subscribe next", { optionCount: options.length });
                setTimeout(() => this.applyInitialVaerkSelection(options));
            });
    }

    private applyInitialVaerkSelection(options: EforsyningOption[]): void {
        const chosen = this.chosenPlant;
        this.logVaerksliste("applyInitialVaerkSelection", {
            optionCount: options.length,
            chosenForsyningId: chosen?.forsyningId
        });
        if (!this.selectedEforsyningId && chosen?.forsyningId) {
            const match = options.find((option) => (option.forsyningId || "").toString().trim() === chosen.forsyningId);
            if (match) {
                this.logVaerksliste("applyInitialVaerkSelection: vælger gemt værk");
                this.onVaerkOptionSelected(match);
                return;
            }
            this.logVaerksliste("applyInitialVaerkSelection: intet match på gemt værk");
        }
        if (options.length === 1) {
            this.logVaerksliste("applyInitialVaerkSelection: auto-vælger eneste værk");
            this.onVaerkOptionSelected(options[0]);
        }
    }

    private normalizeVaerkerResponse(response: unknown): TeknikerVaerkResponse[] {
        if (Array.isArray(response)) {
            this.logVaerksliste("normalizeVaerkerResponse: direkte array", { count: response.length });
            return response as TeknikerVaerkResponse[];
        }
        if (!response || typeof response !== "object") {
            this.logVaerksliste("normalizeVaerkerResponse: tom/ugyldig", { responseType: typeof response });
            return [];
        }

        const payload = response as Record<string, unknown>;
        const listKeys = ["vaerker", "Vaerker", "items", "Items", "result", "Result", "data", "Data", "value", "Value"];
        for (const key of listKeys) {
            const value = payload[key];
            if (Array.isArray(value)) {
                this.logVaerksliste("normalizeVaerkerResponse: wrapper-array", { key, count: value.length });
                return value as TeknikerVaerkResponse[];
            }
        }

        const idCandidate = payload.id ?? payload.Id;
        const nameCandidate = payload.name ?? payload.Name ?? payload.firmaNavn ?? payload.FirmaNavn;
        if (idCandidate !== undefined || nameCandidate !== undefined) {
            this.logVaerksliste("normalizeVaerkerResponse: enkelt objekt", { idCandidate, nameCandidate });
            return [payload as unknown as TeknikerVaerkResponse];
        }

        this.logVaerksliste("normalizeVaerkerResponse: ukendt format", { keys: Object.keys(payload) });
        return [];
    }

    private mapVaerker(vaerker: TeknikerVaerkResponse[]): EforsyningOption[] {
        return (vaerker || [])
            .reduce((acc: EforsyningOption[], vaerk) => {
                const vaerkName = vaerk.firmaNavn || vaerk.name || vaerk.FirmaNavn || vaerk.Name || "";
                const eforsyninger = (vaerk.eforsyninger || vaerk.Eforsyninger || []) as Array<any>;
                if (!eforsyninger.length) {
                    return acc.concat([
                        {
                            id: vaerk.id ?? vaerk.Id ?? null,
                            label: vaerkName,
                            url: vaerk.url || vaerk.Url,
                            appServerUrl: vaerk.appServerUri || vaerk.AppServerUri,
                            forsyningId: vaerk.forsyningId || vaerk.ForsyningId
                        }
                    ]);
                }
                const options = eforsyninger.map((eforsyning) => {
                    const eforsyningId = eforsyning.id ?? eforsyning.Id ?? null;
                    const eforsyningName = eforsyning.name || eforsyning.Name || "";
                    const label = vaerkName && eforsyningName ? `${vaerkName} – ${eforsyningName}` : eforsyningName || vaerkName || "";
                    return {
                        id: eforsyningId,
                        label,
                        url: eforsyning.url || eforsyning.Url,
                        appServerUrl: eforsyning.appServerUri || eforsyning.AppServerUri,
                        forsyningId: eforsyning.forsyningId || eforsyning.ForsyningId
                    };
                });
                return acc.concat(options);
            }, [])
            .filter((option) => option.label)
            .sort((a, b) => compareDanish(a.label, b.label));
    }

    private readChosenPlant(): ChosenPlant | null {
        if (!this.functionalConsentService.hasFunctionalConsent()) {
            return null;
        }
        const raw = this.localStorage.getItem(this.chosenPlantStorageKey);
        if (!raw) {
            return null;
        }
        try {
            const parsed = JSON.parse(raw);
            const forsyningId = String(parsed?.forsyningId ?? "").trim();
            const name = String(parsed?.name ?? "").trim();
            if (!forsyningId || !name) {
                return null;
            }
            const eforsyningId = this.parseStoredEforsyningId(parsed?.eforsyningId ?? parsed?.nodeId);
            const url = String(parsed?.url ?? parsed?.siteUrl ?? "").trim() || undefined;
            const appServerUrl = String(parsed?.appServerUrl ?? parsed?.appServerUri ?? "").trim() || undefined;
            return { forsyningId, name, eforsyningId, url, appServerUrl };
        } catch {
            return null;
        }
    }

    private persistChosenPlant(option: EforsyningOption): void {
        if (!this.functionalConsentService.hasFunctionalConsent()) {
            this.localStorage.removeItem(this.chosenPlantStorageKey);
            return;
        }
        const forsyningId = String(option.forsyningId ?? "").trim();
        const name = String(option.label ?? "").trim();
        const eforsyningId = option.id ?? undefined;
        if (!forsyningId || !name || eforsyningId == null) {
            this.localStorage.removeItem(this.chosenPlantStorageKey);
            return;
        }
        const payload: ChosenPlant = { forsyningId, name, eforsyningId };
        if (option.url) {
            payload.url = option.url;
        }
        if (option.appServerUrl) {
            payload.appServerUrl = option.appServerUrl;
        }
        this.chosenPlant = payload;
        this.localStorage.setItem(this.chosenPlantStorageKey, JSON.stringify(payload));
    }

    private parseStoredEforsyningId(value: unknown): number | undefined {
        const id = typeof value === "number" ? value : parseInt(String(value ?? ""), 10);
        return Number.isFinite(id) && id > 0 ? id : undefined;
    }

    /** ServicePro NUI har top-/bund-bar – ikke klassisk eForsyning-sidemenu. */
    private isServiceProLogin(): boolean {
        const configured = trim(this.appShellLayout?.teknikerLoginRoute || "", "/");
        return configured === trim(SERVICEPRO_TEKNIKER_LOGIN_ROUTE, "/");
    }

    private trackTeknikerLoginSuccess(): void {
        this.matomoTracker.trackEvent(
            "Authentication",
            "Login Success",
            resolveTeknikerLoginMatomoChannel(this.appShellLayout?.teknikerLoginRoute)
        );
    }

    private trackTeknikerLoginFailed(reason: "Invalid Credentials" | "Error"): void {
        const channel = resolveTeknikerLoginMatomoChannel(this.appShellLayout?.teknikerLoginRoute);
        this.matomoTracker.trackEvent("Authentication", "Login Failed", `${channel} - ${reason}`);
    }

    private hentUmbracoIndstillinger(eforsyningId: number): void {
        this.loadingIndstillinger.set(true);
        this.vaerkFejlbesked = "";
        this.logVaerksliste("hentUmbracoIndstillinger: start", {
            eforsyningId,
            serviceProLogin: this.isServiceProLogin()
        });

        const indstillinger$ = this.umbracoIndstillingerRepository.hent(eforsyningId);
        const request$ = this.isServiceProLogin()
            ? indstillinger$.pipe(map((indstillinger) => ({ indstillinger, eforsyningMenu: null, forsyningWebMenu: null })))
            : forkJoin({
                  indstillinger: indstillinger$,
                  eforsyningMenu: this.umbracoHttpClient.get<MenuItem>(`HentEforsyningMenu?id=${eforsyningId}`),
                  forsyningWebMenu: this.umbracoHttpClient.get<MenuItem[]>(`HentForsyningWebMenu?id=${eforsyningId}`)
              });

        request$
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                tap(({ indstillinger, eforsyningMenu, forsyningWebMenu }) => {
                    this.applyUmbracoIndstillinger(indstillinger, eforsyningId);
                    if (!this.isServiceProLogin()) {
                        this.umbracoMenuObserver.next({ eforsyninger: eforsyningMenu, forsyningWeb: forsyningWebMenu });
                    }
                }),
                catchError((error) => {
                    this.logVaerksliste("hentUmbracoIndstillinger: fejl", {
                        status: error?.status,
                        message: error?.message
                    });
                    this.vaerkFejlbesked = "Kunne ikke hente indstillinger for valgt værk.";
                    this.technicianContactPhone = "";
                    return of(null);
                }),
                finalize(() => {
                    this.loadingIndstillinger.set(false);
                    this.logVaerksliste("hentUmbracoIndstillinger: finalize (loadingIndstillinger=false)");
                    if (this.selectedEforsyningId && !this.vaerkFejlbesked) {
                        setTimeout(() => {
                            this.logVaerksliste("hentUmbracoIndstillinger: fokus på ID-felt");
                            this.loginInput?.focus();
                        });
                    }
                })
            )
            .subscribe();
    }

    private applyUmbracoIndstillinger(indstillinger: UmbracoIndstillinger, eforsyningId: number): void {
        const model = Object.assign(new UmbracoIndstillinger(), indstillinger);
        const aktuelEforsyningNodeIdFallback = (model as any).AktuelEforsyningNodeId;
        if (model.aktuelEforsyningNodeId == null && typeof aktuelEforsyningNodeIdFallback === "number") {
            model.aktuelEforsyningNodeId = aktuelEforsyningNodeIdFallback;
        }
        if (model.aktuelEforsyningNodeId == null) {
            model.aktuelEforsyningNodeId = eforsyningId;
        }
        (model.eforsyninger || []).forEach((eforsyning) => {
            const eforsyningAny = eforsyning as any;
            const firmaInfoFallback = eforsyningAny.FirmaInfo;
            if (firmaInfoFallback) {
                eforsyning.firmaInfo = { ...firmaInfoFallback, ...eforsyning.firmaInfo };
            }
            const firmaInfoAny = eforsyning.firmaInfo as any;
            if (firmaInfoAny && firmaInfoAny.telefon == null) {
                firmaInfoAny.telefon = firmaInfoAny.Telefon ?? firmaInfoAny.telefonNr ?? firmaInfoAny.TelefonNr;
            }
            if (firmaInfoAny && firmaInfoAny.firmaNavn == null) {
                firmaInfoAny.firmaNavn = firmaInfoAny.FirmaNavn;
            }
            if (firmaInfoAny && firmaInfoAny.email == null) {
                firmaInfoAny.email = firmaInfoAny.Email;
            }
        });
        this.ensureAktuelEforsyning(model, eforsyningId);
        this.technicianContactPhone = model.aktuelEforsyning?.firmaInfo?.telefon || "";
        this.umbracoIndstillingerObserver.next(model);
    }

    /** Sikrer at aktuelEforsyning kan slås op efter HentUmbracoIndstillinger (ServicePro værksliste). */
    private ensureAktuelEforsyning(model: UmbracoIndstillinger, eforsyningId: number): void {
        model.aktuelEforsyningNodeId = eforsyningId;
        let eforsyning = (model.eforsyninger || []).find((entry) => entry.nodeId === eforsyningId);
        if (!eforsyning) {
            eforsyning = new EforsyningIndstilling();
            eforsyning.nodeId = eforsyningId;
            model.eforsyninger = [...(model.eforsyninger || []), eforsyning];
        }
        const forsyningId = (this.selectedForsyningId ?? eforsyning.forsyningId ?? "").toString().trim();
        if (forsyningId && !eforsyning.forsyningId) {
            eforsyning.forsyningId = forsyningId;
        }
        const appServerUrl = (this.selectedAppServerUrl ?? "").trim();
        if (appServerUrl && !eforsyning.appServerUrl) {
            eforsyning.appServerUrl = appServerUrl;
        }
        const siteUrl = (this.selectedSiteUrl ?? "").trim();
        if (siteUrl && !eforsyning.siteUrl) {
            eforsyning.siteUrl = siteUrl;
        }
    }

    private seedUmbracoIndstillinger(eforsyningId: number, forsyningId?: string | null, siteUrl?: string, appServerUrl?: string): void {
        const resolvedForsyningId = (forsyningId ?? "").toString().trim();
        const model = new UmbracoIndstillinger();
        const eforsyning = new EforsyningIndstilling();
        eforsyning.nodeId = eforsyningId;
        eforsyning.forsyningId = resolvedForsyningId;
        if (siteUrl) {
            eforsyning.siteUrl = siteUrl;
        }
        if (appServerUrl) {
            eforsyning.appServerUrl = appServerUrl;
        }
        model.eforsyninger = [eforsyning];
        model.aktuelEforsyningNodeId = eforsyningId;
        this.umbracoIndstillingerObserver.next(model);
    }

    /** Capability-probe ved værkvalg — afgør om glemt-/skift-felter vises på login. */
    private refreshTeknikerPasswordCapability(forsyningId?: string | null, appServerUrl?: string | null): void {
        const resolvedForsyningId = (forsyningId || "").trim();
        const resolvedAppServerUrl = (appServerUrl || "").trim();
        if (!resolvedForsyningId && !resolvedAppServerUrl) {
            this.clearTeknikerPasswordCapability();
            return;
        }
        this.passwordCapabilityTrigger$.next({
            forsyningId: resolvedForsyningId,
            appServerUrl: resolvedAppServerUrl,
            forceRefresh: true
        });
    }

    private clearTeknikerPasswordCapability(): void {
        this.passwordCapabilityTrigger$.next(null);
    }

    private async initBiometricStatus(): Promise<void> {
        try {
            this.isBiometricAvailable = await this.biometricService.isAvailable();
            this.biometricEnabled = await this.biometricService.isBiometricLoginEnabled();
            this.biometricActionText = await this.biometricService.getBiometricActionText(this.biometricEnabled ? await this.biometricService.getBiometryType() : null);

            sessionStorage.getItem("biometricAutoLoginDone");
            if (!sessionStorage.getItem("biometricAutoLoginDone") && this.biometricEnabled) {
                sessionStorage.setItem("biometricAutoLoginDone", "true");
                setTimeout(() => {
                    this.loginUsingStoredCredentials();
                }, 200);
            }
        } catch {
            this.isBiometricAvailable = false;
            this.biometricEnabled = false;
        }
    }

    public async loginUsingStoredCredentials(): Promise<boolean | void> {
        try {
            const result = await this.biometricService.authenticate();
            if (!result) return false;

            this.loginFejl = false;
            this.fejlBesked = "";
            this.biometricLoginInProgress = true;

            this.loginService
                .login(result.userId, result.secret, false)
                .pipe(first())
                .subscribe({
                    next: async (res) => {
                        this.biometricLoginInProgress = false;
                        if (res.success) {
                            this.trackTeknikerLoginSuccess();
                            await this.onLoginSucceeded();
                        } else {
                            this.trackTeknikerLoginFailed("Invalid Credentials");
                            this.loginFejl = true;
                            this.fejlBesked = "Login mislykkedes";
                        }
                    },
                    error: (err) => {
                        this.biometricLoginInProgress = false;
                        this.trackTeknikerLoginFailed("Error");
                        this.loginFejl = true;
                        this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                    }
                });
        } catch (err: any) {
            this.biometricLoginInProgress = false;
            this.loginFejl = true;
            this.fejlBesked = err?.message ?? "Biometrisk autentificering mislykkedes";
            return false;
        }
    }

    public async biometricLogin(): Promise<void> {
        if (this.biometricLoginInProgress) return;

        this.loginFejl = false;
        this.fejlBesked = "";
        this.biometricLoginInProgress = true;

        try {
            const available = await this.biometricService.isAvailable();
            if (!available) {
                this.loginFejl = true;
                this.fejlBesked = "Biometrisk login virker ikke på denne enhed";
                this.biometricLoginInProgress = false;
                return;
            }

            const alreadyEnabled = await this.biometricService.isBiometricLoginEnabled();

            const email = this.form?.get("emailGroup.email")?.value;
            const password = this.form?.get("emailGroup.adgangskode")?.value;
            const technicianId = this.form?.get("forbrugerNrGroup.forbrugerNr")?.value;
            const technicianCode = this.form?.get("forbrugerNrGroup.kode")?.value;
            const formUserId = email || technicianId;
            const formSecret = password || technicianCode;

            // CASE A: Formular udfyldt med forbrugerNr/email og adgangskode — login med disse oplysninger og spørg om gemme nye oplysninger.
            if (formUserId && formSecret) {
                this.loginService
                    .login(String(formUserId), String(formSecret), false)
                    .pipe(first())
                    .subscribe({
                        next: async (res) => {
                            this.biometricLoginInProgress = false;
                            if (res.success) {
                                this.trackTeknikerLoginSuccess();
                                try {
                                    const wantToStore = confirm(
                                        alreadyEnabled
                                            ? "Vil du opdatere nye loginoplysninger for hurtigere login næste gang? Tryk 'OK' og godkend med biometrisk godkendelse."
                                            : "Vil du gemme loginoplysninger for hurtigere login næste gang? Tryk 'OK' og godkend med biometrisk godkendelse."
                                    );
                                    if (wantToStore) {
                                        try {
                                            await this.biometricService.enableBiometricLogin(
                                                String(formUserId),
                                                String(formSecret),
                                                alreadyEnabled ? "Opdater biometriske loginoplysninger" : "Aktiver biometrisk login",
                                                alreadyEnabled ? "Opdater biometriske loginoplysninger" : "Aktiver biometrisk login",
                                                alreadyEnabled
                                                    ? "Bekræft din identitet for at opdatere dine gemte loginoplysninger sikkert."
                                                    : "Bekræft din identitet for at gemme dine loginoplysninger sikkert. Herefter kan du logge ind hurtigere med finger- eller ansigtsgenkendelse."
                                            );
                                            this.biometricEnabled = true;
                                            await this.onLoginSucceeded();
                                        } catch (err) {
                                            console.warn("Kunne ikke gemme/opdatere biometriske loginoplysninger:", err);
                                            alert("Kunne ikke gemme/opdatere biometriske loginoplysninger.");
                                            await this.onLoginSucceeded();
                                        }
                                    } else {
                                        await this.onLoginSucceeded();
                                    }
                                } catch {}
                            } else {
                                this.trackTeknikerLoginFailed("Invalid Credentials");
                                this.loginFejl = true;
                                this.fejlBesked = "Login mislykkedes";
                            }
                        },
                        error: (err) => {
                            this.biometricLoginInProgress = false;
                            this.trackTeknikerLoginFailed("Error");
                            this.loginFejl = true;
                            this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                        }
                    });
                return;
            }

            // CASE B: Ingen formularoplysninger — forsøg login med gemte biometriske oplysninger.
            const result = await this.biometricService.authenticate("Log ind med biometrisk godkendelse");
            if (!result) {
                this.loginFejl = true;
                this.fejlBesked = "Ingen biometriske loginoplysninger fundet";
                this.biometricLoginInProgress = false;
                return;
            }

            this.loginService
                .login(result.userId, result.secret, false)
                .pipe(first())
                .subscribe({
                    next: async (res) => {
                        this.biometricLoginInProgress = false;
                        if (res.success) {
                            this.trackTeknikerLoginSuccess();
                            await this.onLoginSucceeded();
                        } else {
                            this.trackTeknikerLoginFailed("Invalid Credentials");
                            this.loginFejl = true;
                            this.fejlBesked = "Login mislykkedes";
                        }
                    },
                    error: (err) => {
                        this.biometricLoginInProgress = false;
                        this.trackTeknikerLoginFailed("Error");
                        this.loginFejl = true;
                        this.fejlBesked = err?.friendlyMessage || err?.message || "Login mislykkedes";
                    }
                });
        } catch (err: any) {
            this.biometricLoginInProgress = false;
            const msg = String(err?.message || "").toLowerCase();
            if (msg.includes("cancel") || msg.includes("canceled") || msg.includes("user cancelled")) {
                this.loginFejl = false;
                this.fejlBesked = "";
                return;
            }
            this.loginFejl = true;
            this.fejlBesked = err?.message ?? "Biometrisk autentificering mislykkedes";
            return;
        }
    }
}
